import React, { useState } from 'react';
import { Button, Empty, Image, Input, Modal, Space, Tag, message } from 'antd';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { useAlignedImagePreview } from '../../utils/imagePreviewControls';
import { updateNonStdEntry, voidNonStdEntry } from '../../services/nonstandardOps';
import { ENTRY_KIND_LABEL, NonStdEntry, NonStdEntryPayload } from '../../types/nonstandardOps';
import EntryEditor from './EntryEditor';
import MediaPreviewModal, { MediaPreviewTarget, previewKindOf } from './MediaPreviewModal';

const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;
const VIDEO_EXT = /\.(mp4|mov|webm)$/i;

interface Props {
  entries: NonStdEntry[];
  /** 是否可以修改/作废（操作人且指令状态允许）；后端还会再校验"员工只能改自己的" */
  editable: boolean;
  /** 是否显示"对客户可见"等内部信息 */
  showInternal: boolean;
  /** 管理员可以作废里程碑和客户回复（更正错误） */
  isAdmin?: boolean;
  onChanged: () => void;
}

const KIND_COLOR: Record<string, string> = { note: 'blue', milestone: 'purple', customer_reply: 'magenta' };

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

const EntryTimeline: React.FC<Props> = ({ entries, editable, showInternal, isAdmin, onChanged }) => {
  const [editing, setEditing] = useState<NonStdEntry | null>(null);
  const [previewTarget, setPreviewTarget] = useState<MediaPreviewTarget | null>(null);
  // 照片预览：把左右翻页和关闭按钮挪到照片旁边（默认在屏幕两侧，大屏上离照片很远）
  const onImagePreviewVisibleChange = useAlignedImagePreview();

  const toggleVisible = async (entry: NonStdEntry) => {
    try {
      await updateNonStdEntry(entry.id, { customer_visible: !entry.customer_visible });
      message.success(entry.customer_visible ? '已设为对客户不可见' : '已设为对客户可见');
      onChanged();
    } catch (err: any) {
      message.error(errorText(err, '修改失败'));
    }
  };

  const handleVoid = (entry: NonStdEntry) => {
    let reason = '';
    Modal.confirm({
      title: '作废这条记录？',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>作废后不再显示，但会保留在系统里以便追溯。</div>
          <Input placeholder="作废原因（选填）" onChange={e => { reason = e.target.value; }} />
        </div>
      ),
      okText: '作废',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await voidNonStdEntry(entry.id, reason);
          message.success('已作废');
          onChanged();
        } catch (err: any) {
          message.error(errorText(err, '作废失败'));
          throw err;
        }
      },
    });
  };

  if (entries.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="还没有执行记录" />;
  }

  const rounds = Array.from(new Set(entries.map(e => e.round_no || 1))).sort((a, b) => a - b);
  const showRoundTitles = rounds.length > 1 || entries.some(e => e.kind === 'milestone');

  const renderEntry = (entry: NonStdEntry) => {
    const isMilestone = entry.kind === 'milestone';
    const isReply = entry.kind === 'customer_reply';
    // 里程碑和客户回复不能修改；管理员可以作废它们
    const canEdit = editable && !isMilestone && !isReply;
    const canVoid = editable && (!isMilestone && !isReply ? true : !!isAdmin);
    return (
      <div
        key={entry.id}
        style={{
          borderBottom: '1px solid #f0f0f0',
          padding: '10px 8px',
          background: isMilestone ? '#f9f0ff' : isReply ? '#fff0f6' : undefined,
          borderLeft: isMilestone ? '3px solid #722ed1' : isReply ? '3px solid #eb2f96' : '3px solid transparent',
        }}
      >
        <Space wrap size={8} style={{ marginBottom: 6 }}>
          <Tag color={KIND_COLOR[entry.kind]}>{ENTRY_KIND_LABEL[entry.kind]}</Tag>
          {entry.created_by_name ? <span style={{ fontWeight: 500 }}>{entry.created_by_name}</span> : null}
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>
            <TimezoneAwareTime time={entry.created_at} format="YYYY-MM-DD HH:mm" showTimezone={false} />
          </span>
          {showInternal && entry.customer_visible && !isMilestone && !isReply ? <Tag color="purple">对客户可见</Tag> : null}
          {canEdit ? (
            <Button size="small" onClick={() => toggleVisible(entry)}>
              {entry.customer_visible ? '设为对客户不可见' : '设为对客户可见'}
            </Button>
          ) : null}
          {canEdit ? <a onClick={() => setEditing(entry)}>编辑</a> : null}
          {canVoid ? <a style={{ color: '#cf1322' }} onClick={() => handleVoid(entry)}>作废</a> : null}
        </Space>
        {entry.text ? <div style={{ whiteSpace: 'pre-wrap', fontWeight: isMilestone ? 600 : undefined }}>{entry.text}</div> : null}
        {(entry.attachments || []).length > 0 ? (
          // 附件和上面的提交时间/正文之间留出间距，前面加"附件："标签，一眼能看出这些是这条记录的附件
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span className="nonstd-entry-attachments-label" style={{ flex: 'none', color: '#8c8c8c', lineHeight: '24px' }}>附件：</span>
            <Space wrap style={{ minWidth: 0 }}>
              <Image.PreviewGroup preview={{ onVisibleChange: onImagePreviewVisibleChange }}>
                {entry.attachments.filter(att => IMAGE_EXT.test(att.url)).map(att => (
                  <Image key={att.url} src={getAttachmentUrl({ url: att.url })} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 4 }} />
                ))}
              </Image.PreviewGroup>
              {entry.attachments.filter(att => VIDEO_EXT.test(att.url)).map(att => (
                <video
                  key={att.url}
                  src={getAttachmentUrl({ url: att.url })}
                  controls
                  preload="metadata"
                  style={{ width: 240, maxHeight: 160, background: '#000', borderRadius: 4 }}
                />
              ))}
              {entry.attachments.filter(att => !IMAGE_EXT.test(att.url) && !VIDEO_EXT.test(att.url)).map(att => {
                const url = getAttachmentUrl({ url: att.url });
                const name = att.name || att.url.split('/').pop() || '附件';
                // PDF 和表格（Excel/CSV）在弹窗里预览；Word 等其他格式仍在新标签页打开
                if (previewKindOf(name) || previewKindOf(att.url)) {
                  return <a key={att.url} onClick={() => setPreviewTarget({ url, name })}>{name}</a>;
                }
                return <a key={att.url} href={url} target="_blank" rel="noopener noreferrer">{name}</a>;
              })}
            </Space>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      {rounds.map(round => {
        const group = entries.filter(e => (e.round_no || 1) === round);
        return (
          <div key={round} style={{ marginBottom: 12 }}>
            {showRoundTitles ? (
              <div style={{ fontWeight: 600, color: '#531dab', padding: '6px 8px', background: '#f5f5f5', borderRadius: 4 }}>第 {round} 轮</div>
            ) : null}
            {group.map(renderEntry)}
          </div>
        );
      })}
      <MediaPreviewModal target={previewTarget} onClose={() => setPreviewTarget(null)} />
      <Modal title="编辑记录" open={!!editing} onCancel={() => setEditing(null)} footer={null} destroyOnClose width={640}>
        {editing ? (
          <EntryEditor
            initial={editing}
            submitText="保存修改"
            onCancel={() => setEditing(null)}
            onSubmit={async (payload: NonStdEntryPayload) => {
              await updateNonStdEntry(editing.id, payload);
              message.success('已保存');
              setEditing(null);
              onChanged();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default EntryTimeline;
